import {
  AbdooLogo,
  CalendarContext,
  CategoryChip,
  isBreakpointMobile,
  ReducerContext,
  Typography,
  useBreakpoint,
} from '@abdoo/common';
import { Alert, Box, Grid } from '@mui/material';
import React, { useContext, useMemo } from 'react';

import { getIsEntitlementHealthy } from '../functions/checkHolidayDepletionRate';
import abdooLogo from '../logos/Abdoo logo + safe area_128px-height.png';
import { GraphicProgress } from './GraphicProgress';
import { HomeNavigation } from './HomeNavigation';
interface HomeProps {
  showChart: boolean;
}
export const Home = ({ showChart }: HomeProps) => {
  const { calendarSummaryValues } = useContext(CalendarContext);
  const { userSettingsState } = useContext(ReducerContext);

  const isEntitlementHealthy = useMemo(() => {
    if (calendarSummaryValues !== null) {
      const healthy = getIsEntitlementHealthy(
        userSettingsState.start,
        userSettingsState.end,
        calendarSummaryValues.entitlement,
        calendarSummaryValues.remaining,
      );
      return healthy;
    }

    return true;
  }, [calendarSummaryValues, userSettingsState]);

  const hasUserAnyAbsenceRequestInCurrentEntitlementYear = useMemo(
    () =>
      calendarSummaryValues?.approvedFuture ||
      calendarSummaryValues?.approvedTaken ||
      calendarSummaryValues?.pending,
    [calendarSummaryValues],
  );

  const entitlementAlertText = useMemo(() => {
    if (hasUserAnyAbsenceRequestInCurrentEntitlementYear) {
      return isEntitlementHealthy
        ? 'Well done, you manage your entitlement well across the year.'
        : `To avoid difficulties toward the end of your entitlement year, 
                try to keep your booked entitlement proportionate to your progress through the year.`;
    } else {
      return isEntitlementHealthy
        ? 'To get started, book an absence using the Add Absence button below.'
        : `It looks like you need to book some entitlement. 
                To get started, click the Add Absence button below.`;
    }
  }, [isEntitlementHealthy, hasUserAnyAbsenceRequestInCurrentEntitlementYear]);

  const breakpoint = useBreakpoint();
  const isMobile = isBreakpointMobile(breakpoint);

  const isLoading = !userSettingsState.initialHolidayDataLoaded || !calendarSummaryValues;

  return (
    <Grid
      container
      display={'flex'}
      direction="column"
      alignItems="center"
      justifyContent="center"
    >
      <Grid container maxWidth="20rem" spacing={2} justifyContent="center">
        <Grid container item justifyContent="center" marginTop={'10px'}>
          <AbdooLogo
            src={abdooLogo}
            sx={{
              display: { xs: 'block', md: 'none' },
            }}
          />
        </Grid>
        <Grid spacing={2} container item justifyContent="center">
          <Grid container item justifyContent="center" marginTop={'1px'}>
            <Box display="flex" justifyContent="center" width="100%">
              <Typography
                component="div"
                skeletonHeight={'4em'}
                skeletonWidth="100%"
                isLoading={isLoading}
                variant="body1"
              >
                You have{' '}
                <CategoryChip
                  categoryName="remaining"
                  value={calendarSummaryValues?.remaining ?? 0}
                  isLoading={isLoading}
                />
                days remaining
              </Typography>
            </Box>
          </Grid>
          {showChart && (
            <Grid container item justifyContent="center" marginTop={'0.25em'}>
              <Box height={120} width="100%">
                <GraphicProgress
                  isLoading={isLoading}
                  remaining={calendarSummaryValues?.remaining ?? 0}
                  entitlement={calendarSummaryValues?.entitlement ?? 0}
                  isEntitlementHealthy={isEntitlementHealthy}
                  holidayYearEnd={userSettingsState.end}
                />
              </Box>
            </Grid>
          )}
          <Grid container item>
            <Alert
              sx={{
                backgroundColor: '#e8eaec',
                color: 'black',
                width: '100%',
                maxWidth: '20rem',
                padding: '0.5em',
              }}
              icon={<></>}
            >
              <Typography
                isLoading={isLoading}
                skeletonSx={{ width: 0, height: 0 }}
                component="div"
                variant={isMobile ? 'caption' : 'body2'}
                // eslint-disable-next-line jsx-a11y/aria-role
                role="entitlementAlert"
                aria-describedby="entitlementAlertText"
              >
                <span id="entitlementAlertText">{entitlementAlertText}</span>
              </Typography>
            </Alert>
          </Grid>
          <HomeNavigation isEntitlementHealthy={isEntitlementHealthy} />
        </Grid>
      </Grid>
    </Grid>
  );
};
